import React, { useState, useEffect } from 'react';
import { X, Save, User, AlertTriangle, Mail, Phone, MapPin, Building, Globe, Hash } from 'lucide-react';

interface InfosClient {
  nom: string;
  email: string;
  telephone: string;
  adresse: string;
  notes: string;
  type_client?: string;
  ville?: string;
  code_postal?: string;
  pays?: string;
}

interface ClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  infosClient: InfosClient;
  onSave: (infosClient: InfosClient) => void;
}

const ClientModal: React.FC<ClientModalProps> = ({ 
  isOpen, 
  onClose, 
  infosClient, 
  onSave 
}) => {
  const [formData, setFormData] = useState<InfosClient>({
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    notes: '',
    type_client: 'Particulier',
    ville: '',
    code_postal: '',
    pays: 'France'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [focusedField, setFocusedField] = useState<string>('');

  useEffect(() => {
    setFormData({ 
      nom: infosClient.nom || '',
      email: infosClient.email || '',
      telephone: infosClient.telephone || '',
      adresse: infosClient.adresse || '',
      notes: infosClient.notes || '',
      type_client: infosClient.type_client || 'Particulier',
      ville: infosClient.ville || '',
      code_postal: infosClient.code_postal || '',
      pays: infosClient.pays || 'France'
    });
    setErrors({});
  }, [infosClient, isOpen]);

  // Gestion de la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom du client est requis';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (formData.telephone && !/^[\d\s+\-()]+$/.test(formData.telephone)) {
      newErrors.telephone = 'Numéro de téléphone invalide';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 w-screen h-screen flex items-center justify-center z-[99999] p-4 bg-black/60 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
      <div 
        className="w-full max-w-4xl bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-xl rounded-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-gray-700/50 transform transition-all duration-300 ease-out animate-slideUp" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header moderne */}
        <div className="relative p-8 bg-gradient-to-r from-orange-500/10 to-orange-600/10 border-b border-gray-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-1">
                  Modifier le client
                </h2>
                <p className="text-gray-400">Mettre à jour les informations client</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-12 h-12 rounded-2xl bg-gray-700/50 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all duration-300 flex items-center justify-center backdrop-blur-sm"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8 overflow-y-auto flex-1">
          {/* Type de client */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-300 mb-4">Type de client *</label>
            <div className="flex space-x-4">
              {['Particulier', 'Entreprise', 'Avocat', 'Institution'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type_client: type }))}
                  className={`px-6 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    formData.type_client === type
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                      : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Informations principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nom */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Nom *</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                  onFocus={() => setFocusedField('nom')}
                  onBlur={() => setFocusedField('')}
                  className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                    errors.nom 
                      ? 'border-red-500/50 focus:border-red-500' 
                      : focusedField === 'nom'
                        ? 'border-orange-500/50 focus:border-orange-500'
                        : 'border-gray-700/50 hover:border-gray-600/50'
                  } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                  placeholder="Nom complet du client"
                  required
                />
                <User className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              </div>
              {errors.nom && (
                <div className="flex items-center space-x-2 mt-2 text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{errors.nom}</span>
                </div>
              )}
            </div>

            {/* Email */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField('')}
                  className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                    errors.email 
                      ? 'border-red-500/50 focus:border-red-500' 
                      : focusedField === 'email'
                        ? 'border-orange-500/50 focus:border-orange-500'
                        : 'border-gray-700/50 hover:border-gray-600/50'
                  } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                  placeholder="email@example.com"
                />
                <Mail className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              </div>
              {errors.email && (
                <div className="flex items-center space-x-2 mt-2 text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{errors.email}</span>
                </div>
              )}
            </div>

            {/* Téléphone */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Téléphone</label>
              <div className="relative">
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
                  onFocus={() => setFocusedField('telephone')}
                  onBlur={() => setFocusedField('')}
                  className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                    errors.telephone 
                      ? 'border-red-500/50 focus:border-red-500' 
                      : focusedField === 'telephone'
                        ? 'border-orange-500/50 focus:border-orange-500'
                        : 'border-gray-700/50 hover:border-gray-600/50'
                  } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                  placeholder="+33 1 23 45 67 89"
                />
                <Phone className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              </div>
              {errors.telephone && (
                <div className="flex items-center space-x-2 mt-2 text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{errors.telephone}</span>
                </div>
              )}
            </div>

            {/* Ville */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Ville</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.ville}
                  onChange={(e) => setFormData(prev => ({ ...prev, ville: e.target.value }))}
                  onFocus={() => setFocusedField('ville')}
                  onBlur={() => setFocusedField('')}
                  className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                    focusedField === 'ville'
                      ? 'border-orange-500/50 focus:border-orange-500'
                      : 'border-gray-700/50 hover:border-gray-600/50'
                  } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                  placeholder="Paris"
                />
                <Building className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              </div>
            </div>

            {/* Code postal */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Code postal</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.code_postal}
                  onChange={(e) => setFormData(prev => ({ ...prev, code_postal: e.target.value }))}
                  onFocus={() => setFocusedField('code_postal')}
                  onBlur={() => setFocusedField('')}
                  className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                    focusedField === 'code_postal'
                      ? 'border-orange-500/50 focus:border-orange-500'
                      : 'border-gray-700/50 hover:border-gray-600/50'
                  } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                  placeholder="75001"
                />
                <Hash className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              </div>
            </div>

            {/* Pays */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Pays</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.pays}
                  onChange={(e) => setFormData(prev => ({ ...prev, pays: e.target.value }))}
                  onFocus={() => setFocusedField('pays')}
                  onBlur={() => setFocusedField('')}
                  className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                    focusedField === 'pays'
                      ? 'border-orange-500/50 focus:border-orange-500'
                      : 'border-gray-700/50 hover:border-gray-600/50'
                  } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                  placeholder="France"
                />
                <Globe className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              </div>
            </div>
          </div>

          {/* Adresse complète */}
          <div className="relative group">
            <label className="block text-sm font-medium text-gray-300 mb-2">Adresse</label>
            <div className="relative">
              <input
                type="text"
                value={formData.adresse}
                onChange={(e) => setFormData(prev => ({ ...prev, adresse: e.target.value }))}
                onFocus={() => setFocusedField('adresse')}
                onBlur={() => setFocusedField('')}
                className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                  focusedField === 'adresse'
                    ? 'border-orange-500/50 focus:border-orange-500'
                    : 'border-gray-700/50 hover:border-gray-600/50'
                } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                placeholder="123 rue de la Paix"
              />
              <MapPin className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
            </div>
          </div>

          {/* Notes */}
          <div className="relative group">
            <label className="block text-sm font-medium text-gray-300 mb-2">Notes sur le client</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              onFocus={() => setFocusedField('notes')}
              onBlur={() => setFocusedField('')}
              className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                focusedField === 'notes'
                  ? 'border-orange-500/50 focus:border-orange-500'
                  : 'border-gray-700/50 hover:border-gray-600/50'
              } focus:outline-none focus:ring-4 focus:ring-orange-500/20 resize-none`}
              placeholder="Notes sur le client..."
              rows={4}
            />
            <p className="text-xs text-gray-500 mt-2">
              Informations importantes, préférences, historique des interactions...
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-8 border-t border-gray-700/30">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white rounded-2xl font-medium transition-all duration-300 backdrop-blur-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Save className="w-5 h-5" />
              <span>Modifier</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientModal; 